import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDeleteCompetence,
  useGetCompetences,
  useUpdateCompetence,
} from '@legacy/apis/competencesAPI';
import {
  IConvertedRow,
  TCompetenceState,
} from '@legacy/apis/competencesAPI/types';
import {
  Box,
  Button,
  DataGrid,
  IconButton,
  Select,
  Stack,
  TextField,
  ToastifyAlert,
  Typography,
} from '@legacy/components/mui-components';

import { CheckCircle, Circle, Delete, Edit } from '@mui/icons-material';
import { MenuItem } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { visuallyHidden } from '@mui/utils';
import {
  GridColDef,
  GridEventListener,
  GridRenderCellParams,
} from '@mui/x-data-grid-pro';

import { toast } from 'react-toastify';
import {
  useDeleteCompetenceGroups,
  useGetCompetenceGroups,
} from '@legacy/apis/competenceGroupAPI';
import { SettingsContentWrapper } from '@legacy/components/layout/SettingsContentWrapper';
import { HighlightMatchingText } from '@legacy/components/utils/HighlightMatchingText';
import { useDebounce } from 'use-debounce';
import { DeleteDialog } from '../../components';
import {
  NewCompetenceCategoryDialog,
  NewCompetenceDialog,
  Toolbar,
} from './components';

// Density selector and export button
const slots = {
  toolbar: Toolbar,
};

export const Competences = () => {
  const { t } = useTranslation('systemAdministrationEmployeesCompetences');

  // Filters
  const [viewStatus, setViewStatus] = useState<TCompetenceState>('all');

  const handleViewChange = (event: SelectChangeEvent) => {
    setViewStatus(event.target.value as TCompetenceState);
  };

  const [viewCategory, setViewCategory] = useState<string>('-1');

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setViewCategory(event.target.value);
  };

  const [queryString, setQueryString] = useState<string>('');
  const [debouncedQueryString] = useDebounce(queryString, 250);

  const onSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setQueryString(event.target.value);
  };

  // States
  const [competenceCategoryIsOpen, setCompetenceCategoryIsOpen] =
    useState(false);
  const [competenceIsOpen, setCompetenceIsOpen] = useState(false);
  const [deleteIsOpen, setDeleteIsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Data
  const initialRowInfo: IConvertedRow = useMemo(
    () => ({
      id: '',
      apiId: -1,
      name: '',
      category: -1,
      isActive: undefined,
      canBeDeleted: undefined,
      items: [],
    }),
    [],
  );
  const [rowInfo, setRowInfo] = useState<IConvertedRow>(initialRowInfo);

  const { rows, isLoading } = useGetCompetences();

  const filteredData = useMemo(() => {
    if (!rows) return [];
    const filteredRows = rows.filter((item) => {
      const isQueryMatching =
        item.name?.toLowerCase().includes(debouncedQueryString.toLowerCase()) ||
        item.items?.some((i) =>
          i.name?.toLowerCase().includes(debouncedQueryString.toLowerCase()),
        );
      const isCategoryMatching =
        viewCategory === '-1' ||
        item.category === parseInt(viewCategory, 10) ||
        (item.type === 'category' && item.apiId === parseInt(viewCategory, 10));
      const isStatusMatching =
        viewStatus === 'all' ||
        item.isActive === (viewStatus === 'active') ||
        item.items?.some((i) => i.isActive === (viewStatus === 'active'));

      return isQueryMatching && isCategoryMatching && isStatusMatching;
    });

    return filteredRows;
  }, [rows, debouncedQueryString, viewCategory, viewStatus]);

  const { categories } = useGetCompetenceGroups();

  const { mutate: updateCompetence } = useUpdateCompetence();
  const { mutate: deleteCompetence } = useDeleteCompetence();
  const { mutate: deleteCompetenceGroup } = useDeleteCompetenceGroups();

  // Actions
  const handleUpdateStatus = (row: IConvertedRow) => {
    updateCompetence(
      {
        id: row.apiId,
        name: row.name,
        isActive: !row.isActive,
        groupId: row.category,
      },
      {
        onSuccess: () => {
          toast(
            <ToastifyAlert
              title={t('modals.CompetenceEditToastTitle', {
                COMPETENCE_NAME: row.name,
              })}
              color="success"
              data-automation-id="EditCompetenceDialogToastSuccess"
            />,
            { autoClose: 4000, closeButton: false },
          );
        },
        onError: (error) => {
          toast(
            <ToastifyAlert
              title={t('modals.CompetenceEditToastTitleError', {
                COMPETENCE_NAME: row.name,
              })}
              description={`${error}`}
              color="error"
              data-automation-id="EditCompetenceDialogToastSuccess"
            />,
            { closeButton: true },
          );
        },
      },
    );
  };

  const handleDelete = (row: IConvertedRow) => {
    setRowInfo(row);
    setDeleteIsOpen(true);
  };

  useEffect(() => {
    if (!deleteIsOpen) {
      setRowInfo(initialRowInfo);
    }
  }, [deleteIsOpen, initialRowInfo]);

  const handleEdit = (row: IConvertedRow) => {
    setIsEditing(true);
    setRowInfo(row);
    if (row.category) {
      setCompetenceIsOpen(true);
    } else {
      setCompetenceCategoryIsOpen(true);
    }
  };

  const handleDoubleClickEvent: GridEventListener<'cellDoubleClick'> = (
    params,
    event,
  ) => {
    event.preventDefault();

    if (params.field === 'name') {
      handleEdit(params.row as IConvertedRow);
    }
  };

  // Table preparations
  const columns: GridColDef[] = [
    {
      field: 'name',
      flex: 1,
      minWidth: 200,
      headerName: t('TableColumnHeaderTextName'),
      display: 'flex',
      cellClassName: 'textEllipsis',
      renderCell: ({ row }) => (
        // userSelect: 'none' is used to prevent text selection on double click
        <Typography
          fontWeight={row.category ? 400 : 600}
          sx={{ userSelect: 'none' }}
        >
          <HighlightMatchingText name={row.name} matchName={queryString} />
        </Typography>
      ),
      sortable: false,
    },
    {
      field: 'active',
      type: 'string',
      width: 100,
      headerName: t('TableColumnHeaderTextStatus'),
      headerAlign: 'center',
      align: 'center',
      display: 'flex',
      renderCell: ({ row }: GridRenderCellParams<IConvertedRow>) => {
        if (row.type === 'category') {
          return null;
        }

        if (row.isActive) {
          return (
            <IconButton
              data-automation-id={`CompetencesStatusActiveButton${row.apiId}`}
              onClick={() => handleUpdateStatus(row)}
              title={t('CheckmarkIconTooltipText')}
            >
              <CheckCircle color="success" />
            </IconButton>
          );
        }

        return (
          <IconButton
            data-automation-id={`CompetencesStatusInactiveButton${row.apiId}`}
            onClick={() => handleUpdateStatus(row)}
            title={t('CircleIconTooltipText')}
          >
            <Circle color="disabled" />
          </IconButton>
        );
      },
      sortable: false,
    },
    {
      field: 'actions',
      width: 100,
      renderHeader: () => (
        <Box sx={visuallyHidden}>
          {t('TableColumnHeaderScreenReaderTextActions')}
        </Box>
      ),
      align: 'center',
      display: 'flex',
      renderCell: ({ row }) => {
        const disabled = row.items?.length > 0 || !row.canBeDeleted;
        const isCategory = row.type === 'category';
        const disabledText = isCategory
          ? t('CategoryCanNotBeDeletedReasonTooltipText')
          : t('CompetenceCanNotBeDeletedReasonTooltipText');
        return (
          <Stack direction="row">
            <IconButton
              title={t('EditTooltipText', { COMPETENCE_NAME: row.name })}
              onClick={() => handleEdit(row)}
              data-automation-id={`${isCategory ? 'Category' : 'Competences'}EditButton${
                row.apiId
              }`}
            >
              <Edit fontSize="small" />
            </IconButton>
            <IconButton
              disabled={disabled}
              title={
                disabled
                  ? disabledText
                  : t('DeleteTooltipText', { COMPETENCE_NAME: row.name })
              }
              onClick={() => handleDelete(row)}
              data-automation-id={`${isCategory ? 'Category' : 'Competences'}DeleteButton${
                row.apiId
              }`}
            >
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        );
      },
      sortable: false,
    },
  ];

  return (
    <SettingsContentWrapper
      header={t('PageHeader')}
      description={t('PageDescription')}
    >
      <Stack gap={2}>
        <Stack
          direction="row"
          flexWrap="wrap"
          gap={2}
          justifyContent="space-between"
          alignItems="center"
        >
          <Stack direction="row" flexWrap="wrap">
            <TextField
              value={queryString}
              onChange={onSearchChange}
              label={t('SearchInputLabelText')}
              ariaLabel={t('SearchInputLabelText')}
              data-automation-id="CompetencesSearchInput"
              size="small"
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
            />

            <Select
              value={viewStatus}
              size="small"
              label={t('ViewOptionStatusLabel')}
              onChange={handleViewChange}
              autoWidth
              data-automation-id="CompetencesStatusSelect"
            >
              <MenuItem
                value="all"
                data-automation-id="CompetencesStatusItemAll"
              >
                {t('ViewOptionStatusAllText')}
              </MenuItem>
              <MenuItem
                value="active"
                data-automation-id="CompetencesStatusItemActive"
              >
                {t('ViewOptionStatusActiveText')}
              </MenuItem>
              <MenuItem
                value="inactive"
                data-automation-id="CompetencesStatusItemInactive"
              >
                {t('ViewOptionStatusInActiveText')}
              </MenuItem>
            </Select>

            <Select
              value={viewCategory}
              size="small"
              label={t('ViewOptionCategoryLabel')}
              onChange={handleCategoryChange}
              autoWidth
              data-automation-id="CompetencesCategorySelect"
            >
              <MenuItem value="-1">
                {t('ViewOptionCategoryAllCategories')}
              </MenuItem>
              {categories?.map(({ properties: categoriesProperties }) => (
                <MenuItem
                  key={`filter-category-option-${categoriesProperties?.groupId}`}
                  data-automation-id={`CompetencesCategorySelectItem${categoriesProperties?.groupId}`}
                  value={categoriesProperties?.groupId}
                >
                  {categoriesProperties?.groupName}
                </MenuItem>
              ))}
            </Select>
          </Stack>
          <Stack direction="row" flexWrap="wrap" spacing={1}>
            <Button
              variant="contained"
              onClick={() => setCompetenceCategoryIsOpen(true)}
              data-automation-id="SystemAdministrationCreateCompetenceCategoryButton"
            >
              {t('CreateNewCompetenceCategoryButtonText')}
            </Button>

            <Button
              variant="contained"
              onClick={() => setCompetenceIsOpen(true)}
              data-automation-id="SystemAdministrationCreateCompetenceButton"
            >
              {t('CreateNewCompetenceButtonText')}
            </Button>
          </Stack>
        </Stack>

        <div style={{ width: '100%' }}>
          <DataGrid
            disableColumnMenu
            disableColumnTopBorder
            disableRowSelectionOnClick
            disableColumnResize
            autoHeight
            columns={columns}
            rows={filteredData}
            slots={slots}
            loading={isLoading}
            pagination
            pageSizeOptions={[20, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: {
                  pageSize: 20,
                },
              },
            }}
            onCellDoubleClick={handleDoubleClickEvent}
            slotProps={{
              root: {
                'data-automation-id': 'CompetencesDataGrid',
              },
            }}
          />
        </div>

        {/* Dialogs */}
        {deleteIsOpen && (
          <DeleteDialog
            open={deleteIsOpen}
            closeDialog={() => setDeleteIsOpen(false)}
            itemId={rowInfo.apiId ?? -1}
            name={rowInfo.name ?? ''}
            deleteFunction={
              rowInfo.type === 'competence'
                ? deleteCompetence
                : deleteCompetenceGroup
            }
            headerOverride={
              rowInfo.type === 'competence'
                ? t('DeleteCompetenceHeader')
                : t('DeleteCategoryHeader')
            }
            contentOverride={
              rowInfo.type === 'competence'
                ? t('DeleteCompetenceContent', {
                    COMPETENCE_NAME: rowInfo.name,
                  })
                : t('DeleteCategoryContent', { CATEGORY_NAME: rowInfo.name })
            }
          />
        )}

        {competenceCategoryIsOpen && (
          <NewCompetenceCategoryDialog
            open={competenceCategoryIsOpen}
            closeDialog={() => setCompetenceCategoryIsOpen(false)}
            rowInfo={rowInfo}
            setRowInfo={setRowInfo}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        )}

        {competenceIsOpen && (
          <NewCompetenceDialog
            open={competenceIsOpen}
            closeDialog={() => setCompetenceIsOpen(false)}
            rowInfo={rowInfo}
            setRowInfo={setRowInfo}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
          />
        )}
      </Stack>
    </SettingsContentWrapper>
  );
};
